import { ChakraProvider } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AppProps } from 'next/app';
import { useEffect, useState } from 'react';
import Script from 'next/script';
import LogRocket from 'logrocket';

import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import "react-day-picker/style.css";

import { RErrorBoundary } from 'components';
import {
  RootContext,
  AuthProvider,
  NotificationProvider,
  MarketPlaceProvider,
  WalletProvider,
  AnimatedTransitionProvider,
  UserProvider,
  TourProvider,
  TuningProvider,
  VideoProvider,
  LoadingTrailerProvider,
  ResultsProvider,
  CheckoutProvider,
} from 'context';
import theme from 'utils/baseTheme';
import { HubProvider } from 'context/hubContext';
import { SessionDataProvider } from 'hooks/useSessionData';
import WalletLoading from 'components/Wallet/WalletLoading';
import RainbowKitWagmiConfig from 'hooks/WalletConnect/RainbowKitWagmiConfig';

import '../utils/globals.css';
import { CrossmintCheckoutProvider, CrossmintProvider } from '@crossmint/client-sdk-react-ui';

if (process.env.NEXT_PUBLIC_ENV === 'prod') LogRocket.init('ix1njk/racino');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const apiKey = process.env.NEXT_PUBLIC_CROSSMINT_API_KEY || '';

const CustomApp = ({ Component, pageProps }: AppProps) => {
  const [isDebug, setIsDebug] = useState(false);
  const [subdomain, setSubdomain] = useState('');

  console.log('NODE_ENV', process.env.NODE_ENV);

  useEffect(() => {
    if (Number(localStorage.getItem('debug')) === 1) {
      setIsDebug(true);
    }
    setSubdomain(window.location.hostname.split('.').slice(0, -2).join('.'));
  }, []);

  useEffect(() => {
    window.__RACINO__ = {
      logToken: () => {
        const sessionData = localStorage.getItem('sessionData');
        if (sessionData) {
          console.log(JSON.parse(sessionData).accessToken);
        }
      },
    };
  }, []);

  return (
    <SessionDataProvider>
      <QueryClientProvider client={queryClient}>
        <HubProvider>
          <MarketPlaceProvider>
            <NotificationProvider>
              <AuthProvider>
                <RainbowKitWagmiConfig>
                  <WalletProvider>
                    <CrossmintProvider apiKey={apiKey}>
                      <CrossmintCheckoutProvider>
                        <CheckoutProvider>
                          <ReactQueryDevtools />
                          <ChakraProvider theme={theme}>
                            <UserProvider>
                              <AnimatedTransitionProvider>
                                <TourProvider>
                                  <RootContext.Provider
                                    value={{ isDebug, subdomain }}
                                  >
                                    <LoadingTrailerProvider>
                                      <ResultsProvider>
                                        <TuningProvider>
                                          <RErrorBoundary>
                                            <WalletLoading />
                                            <VideoProvider
                                              videoUrls={[
                                                '/videos/reveal-rewards-desktop.webm',
                                                '/videos/reveal-rewards-mobile.webm',
                                              ]}
                                            >
                                              <Component {...pageProps} />
                                            </VideoProvider>
                                          </RErrorBoundary>
                                        </TuningProvider>
                                      </ResultsProvider>
                                    </LoadingTrailerProvider>
                                  </RootContext.Provider>
                                </TourProvider>
                              </AnimatedTransitionProvider>
                            </UserProvider>
                          </ChakraProvider>
                        </CheckoutProvider>
                      </CrossmintCheckoutProvider>
                    </CrossmintProvider>
                  </WalletProvider>
                </RainbowKitWagmiConfig>
              </AuthProvider>
            </NotificationProvider>
          </MarketPlaceProvider>
        </HubProvider>
      </QueryClientProvider>
    </SessionDataProvider>
  );
};

export default CustomApp;
